/**
 * Image Optimization Utilities
 * Handles resizing, compression, and WebP conversion for uploaded images
 */

export interface OptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1
  format?: 'webp' | 'jpeg' | 'png';
  maintainAspectRatio?: boolean;
}

export interface OptimizationResult {
  blob: Blob;
  format: string;
  originalSize: number;
  optimizedSize: number;
  compressionRatio: number;
  width: number;
  height: number;
}

/**
 * Load an image file into an HTMLImageElement
 */
export function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
export function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;
  
  // Check if resizing is needed
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }
  
  // Calculate aspect ratio
  const aspectRatio = width / height;
  
  // Resize based on the limiting dimension
  if (width > maxWidth) {
    width = maxWidth;
    height = Math.round(width / aspectRatio);
  }
  
  if (height > maxHeight) {
    height = maxHeight;
    width = Math.round(height * aspectRatio);
  }
  
  return { width, height };
}

/**
 * Check if browser supports WebP
 */
export function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
}

/**
 * Optimize an image file
 */
export async function optimizeImage(
  file: File,
  options: OptimizationOptions = {}
): Promise<OptimizationResult> {
  const {
    maxWidth = 512,
    maxHeight = 512,
    quality = 0.85,
    format = 'webp',
    maintainAspectRatio = true,
  } = options;
  
  const originalSize = file.size;
  
  // Load the image
  const img = await loadImage(file);
  
  // Calculate new dimensions
  const dimensions = maintainAspectRatio
    ? calculateDimensions(img.naturalWidth, img.naturalHeight, maxWidth, maxHeight)
    : { width: maxWidth, height: maxHeight };
  
  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }
  
  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Draw the image
  ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);
  
  // Determine output format
  let mimeType: string;
  let outputFormat: string;
  
  if (format === 'webp' && supportsWebP()) {
    mimeType = 'image/webp';
    outputFormat = 'webp';
  } else if (format === 'jpeg' || file.type === 'image/jpeg') {
    mimeType = 'image/jpeg';
    outputFormat = 'jpeg';
  } else {
    mimeType = 'image/png';
    outputFormat = 'png';
  }
  
  // Convert canvas to blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      mimeType,
      quality
    );
  });
  
  const optimizedSize = blob.size;
  const compressionRatio = ((originalSize - optimizedSize) / originalSize) * 100;
  
  return {
    blob,
    format: outputFormat,
    originalSize,
    optimizedSize,
    compressionRatio,
    width: dimensions.width,
    height: dimensions.height,
  };
}

/**
 * Optimize logo specifically (smaller dimensions, higher quality)
 */
export async function optimizeLogo(file: File): Promise<OptimizationResult> {
  return optimizeImage(file, {
    maxWidth: 512,
    maxHeight: 512,
    quality: 0.9,
    format: 'webp',
    maintainAspectRatio: true,
  });
}

/**
 * Optimize banner/hero image (larger dimensions, good quality)
 */
export async function optimizeBanner(file: File): Promise<OptimizationResult> {
  return optimizeImage(file, {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.85,
    format: 'webp',
    maintainAspectRatio: true,
  });
}

/**
 * Optimize thumbnail (small dimensions, lower quality acceptable)
 */
export async function optimizeThumbnail(file: File): Promise<OptimizationResult> {
  return optimizeImage(file, {
    maxWidth: 256,
    maxHeight: 256,
    quality: 0.8,
    format: 'webp',
    maintainAspectRatio: true,
  });
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Validate image file
 */
export function validateImageFile(
  file: File,
  options: {
    maxSize?: number; // in bytes
    allowedTypes?: string[];
  } = {}
): { valid: boolean; error?: string } {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  } = options;
  
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}`,
    };
  }
  
  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${formatFileSize(maxSize)}`,
    };
  }
  
  return { valid: true };
}
